import React from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemaKeuze from '../ThemaKeuze';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent } from '../../utils/analytics';

interface PublicPageFooterProps {
  /** Waar de pagina over gaat: bepaalt de zin in de promo en de bron in de telling. */
  variant: 'opstelling' | 'live';
  /** Bovenaan in de voet, zoals de wedstrijdkiezer van de opstelling. */
  children?: React.ReactNode;
}

// De bronnen van de opstelling bestonden al; die blijven gelijk, anders breekt de reeks in
// Plausible.
const PROMO = {
  opstelling: { madeWith: 'promo.madeWith', href: '/?bron=opstelling', bron: 'spelerspagina' },
  live: { madeWith: 'promo.madeWithLive', href: '/?bron=live-verslag', bron: 'live-verslag' },
} as const;

/**
 * De donkere voet van de publieke pagina's: de promo voor wie niet is ingelogd en de keuze
 * voor taal en thema. Gedeeld door de opstelling en de volgpagina van het live verslag.
 */
export const PublicPageFooter: React.FC<PublicPageFooterProps> = ({ variant, children }) => {
  const { t } = useTranslation('playerView');
  const { isAuthenticated } = useAuth();
  const promo = PROMO[variant];

  return (
    <footer className="bg-vast-donker text-white py-8 mt-12">
      <div className="max-w-6xl mx-auto px-4">
        {children}

        {/* Deze pagina wordt per wedstrijd gedeeld met spelers en ouders - vaak
            tientallen mensen die verder niets van de app weten. Dit is de enige plek
            waar zij horen wat dit is. Alleen tonen aan wie niet is ingelogd: een coach
            die zijn eigen opstelling bekijkt heeft er niets aan. */}
        {!isAuthenticated && (
          <div className="border-t border-white/15 pt-6 pb-2">
            <div className="text-center">
              <p className="text-sm text-white/75">
                {t(promo.madeWith)}{' '}
                <span className="font-semibold text-white">Club Coach App</span>
              </p>
              <p className="mt-1 text-sm text-white/60">
                {t('promo.pitch')}
              </p>
              <a
                href={promo.href}
                onClick={() => trackEvent('Publieke Pagina CTA', { bron: promo.bron })}
                className="mt-4 inline-flex items-center rounded-lg bg-hockey-blue px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-400"
              >
                {t('promo.cta')}
              </a>
            </div>
          </div>
        )}

        {/* De taalkeuze hoort hier omdat deze route geen inlog heeft: een Engelstalige
            ouder die de deel-link krijgt, kan de taal nergens anders omzetten. */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 border-t border-white/15 pt-6">
          <LanguageSwitcher variant="dark" />
          <ThemaKeuze variant="dark" />
        </div>
      </div>
    </footer>
  );
};
